import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Avatar,
  Chip,
  FormControl,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { KeyboardArrowDown, CheckCircle } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { centersDropdownRoute } from '../../../routes/apiRoutes';
import axiosInstance from '../../../axios/axiosInstance';


const CenterSelector = ({ 
  selectedCenter, 
  onCenterChange, 
  collapsed = false,
  centers = [] 
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [centersData, setCentersData] = useState([]);
  const [loadingCenters, setLoadingCenters] = useState(false);
  const [error, setError] = useState(null);
  const [isChanging, setIsChanging] = useState(false);
  const autoSwitchedRef = useRef(false);
  
  const currentUser = useSelector((state) => state.authenticationValues.currentUser);
  const roles = React.useMemo(
    () => currentUser.roles?.map((roleItem) => roleItem?.name) || [],
    [currentUser]
  );
  const isSingleLocation = currentUser.currentLocation && currentUser.locationIds && currentUser.locationIds.length === 1;
  const currentLocation = currentUser.currentLocation ? {
    id: currentUser.currentLocation.id,
    code: currentUser.currentLocation.code || currentUser.currentLocation.location_name.substring(0, 2).toUpperCase(),
    name: currentUser.currentLocation.location_name,
    color: currentUser.currentLocation.colorPalette || '#BB2436', // Default green if no color
    logo: currentUser.currentLocation.logo || null, // Include logo from current location
  } : {};
  const isSuperAdmin = React.useMemo(
    () => roles.includes("Super Admin"),
    [roles, currentUser]
  );

  // Fetch centers from API
  useEffect(() => {
    if (!isSingleLocation) {
      fetchCenters();
    }
  }, []);

  // Listen for global refresh events (e.g., after creating/updating/deleting a center)
  useEffect(() => {
    if (isSingleLocation) return; // nothing to refresh when only one location is allowed
    const handleCentersUpdated = () => {
      fetchCenters();
    };
    window.addEventListener('centers-updated', handleCentersUpdated);
    return () => {
      window.removeEventListener('centers-updated', handleCentersUpdated);
    };
  }, [isSingleLocation]);

  const fetchCenters = async () => {
    try {
      setLoadingCenters(true);
      setError(null);
      // Assignment-based list — does not require Locations management permission
      const response = await axiosInstance.get(centersDropdownRoute, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.data?.status === "success") {
        const centers = response.data.data || [];
        const transformedCenters = centers.map(center => ({
          id: center.id,
          code: center.code || center.location_name.substring(0, 2).toUpperCase(),
          name: center.location_name,
          color: center.colorPalette || '#BB2436',
          logo: center.logo || null,
          isActive: center.is_active !== false,
          originalData: center
        }));
        // API already scopes by user_locations; keep client filter as defense-in-depth
        const assignedIds = currentUser.locationIds || [];
        const usersAssignedCenters = isSuperAdmin
          ? transformedCenters
          : transformedCenters.filter(
              (center) =>
                assignedIds.length === 0 || assignedIds.includes(center.id)
            );
        setCentersData(usersAssignedCenters);
      } else {
        const errorMsg = response.data?.message || 'Failed to fetch centers';
        console.error('Failed to fetch centers:', response.data);
        setError(errorMsg);
        setCentersData([]);
      }
    } catch (error) {
      console.error('Error fetching centers:', error);
      const errorMsg = error.response?.data?.message || 'Failed to fetch centers';
      setError(errorMsg);
      setCentersData([]);
    } finally {
      setLoadingCenters(false);
    }
  };

  // Refresh centers function
  const handleRefresh = () => {
    fetchCenters();
  };

  // Build options (API data > props > current location)
  let centerOptions = centersData.length > 0 ? centersData : 
                     (centers.length > 0 ? centers : [currentLocation]);

  // Prepend All Centers option for Super Admins
  const allCentersOption = isSuperAdmin ? {
    id: 'ALL',
    code: 'ALL',
    name: 'All Centers',
    color: '#BB2436',
    logo: null,
    isAllCenters: true,
  } : null;
  if (isSuperAdmin) {
    centerOptions = [allCentersOption, ...centerOptions];
  }

  // Default to first center if none selected
  const currentCenter = selectedCenter || centerOptions[0];

  // If the user no longer has access to the currently selected center,
  // automatically switch them to the first allowed center.
  useEffect(() => {
    if (!onCenterChange) return;
    if (isSuperAdmin) return; // super admins can access all centers
    if (loadingCenters || isChanging) return;
    if (autoSwitchedRef.current) return;

    const allowed = (centersData.length > 0 ? centersData : centerOptions).filter(Boolean);
    if (!allowed || allowed.length === 0) return;

    const currentId = currentUser?.currentLocation?.id || selectedCenter?.id;
    if (!currentId) return;

    const hasAccess = allowed.some((c) => c.id === currentId);
    if (hasAccess) return;

    autoSwitchedRef.current = true;
    // Switch to first allowed center
    const fallback = allowed[0];
    setIsChanging(true);
    Promise.resolve(onCenterChange(fallback))
      .catch(() => {
        // allow retry if switching fails
        autoSwitchedRef.current = false;
      })
      .finally(() => {
        setIsChanging(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSuperAdmin,
    loadingCenters,
    isChanging,
    centersData,
    currentUser?.currentLocation?.id,
    selectedCenter?.id,
    onCenterChange,
  ]);

  const handleCenterChange = async (event) => {
    const centerId = event.target.value;
    const center = centerOptions.find(c => c.id === centerId);
    if (center && onCenterChange) {
      setIsChanging(true);
      try {
        const maybePromise = onCenterChange(center);
        await Promise.resolve(maybePromise);
      } finally {
        setIsChanging(false);
        setOpen(false);
      }
    }
  };

  const showInlineLoading = loadingCenters || isChanging;

  if (collapsed) {
    // Show only icon when collapsed
    return (
      <Tooltip title={currentCenter?.name || 'Select Center'} placement="right" arrow>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 1,
            mb:1,
            borderRadius: 1,
            cursor: showInlineLoading ? 'default' : 'pointer',
            '&:hover': {
              backgroundColor: !showInlineLoading ? theme.palette.action.hover : 'transparent',
              borderRadius: 1,
            },
            boxShadow: theme.shadows[1],
          }}
          onClick={() => !showInlineLoading && setOpen(true)}
        >
          {showInlineLoading ? (
            <CircularProgress size={24} color="primary" />
          ) : (
            <Avatar
              src={currentCenter?.logo || undefined}
              sx={{
                width: 30,
                height: 30,
                bgcolor: currentCenter?.color || theme.palette.primary.main,
                fontSize: '0.75rem',
                fontWeight: 600,
              }}
            >
              {currentCenter?.code || 'C'}
            </Avatar>
          )}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box
      sx={{
        p: 1,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      {centerOptions.length > 1 ? (
        <FormControl fullWidth size="small">
          <Select
            value={currentCenter?.id || ''}
            onChange={handleCenterChange}
            onOpen={() => !showInlineLoading && setOpen(true)}
            onClose={() => setOpen(false)}
            open={open}
            displayEmpty
            disabled={showInlineLoading}
            IconComponent={showInlineLoading ? () => <></> : KeyboardArrowDown}
            sx={{
              backgroundColor: theme.palette.background.default,
              boxShadow: theme.shadows[1],
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1,
                px: 1.5,
                pr: 4, // ensure space for the dropdown arrow
                minWidth: 0, // allow content to shrink
              },
              '& fieldset': {
                border: 'none',
              },
              '&:hover': {
                backgroundColor: !showInlineLoading ? theme.palette.action.hover : 'transparent',
                borderRadius: 1,
              },
              '&.Mui-focused': {
                backgroundColor: !showInlineLoading ? theme.palette.action.hover : 'transparent',
                borderRadius: 1,
              },
              '&.Mui-disabled': {
                backgroundColor: 'transparent',
              },
            }}
            renderValue={(value) => {
              if (showInlineLoading) {
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <CircularProgress size={20} color="primary" />
                    <Typography variant="body2" color="text.secondary">
                      Switching center...
                    </Typography>
                  </Box>
                );
              }
              
              if (error) {
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography variant="body2" color="error" sx={{ fontSize: '0.75rem' }}>
                      Error loading centers
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRefresh();
                      }}
                      sx={{ p: 0.5 }}
                    >
                      <Typography variant="caption" color="primary">
                        Retry
                      </Typography>
                    </IconButton>
                  </Box>
                );
              }
              
              const center = centerOptions.find(c => c.id === value);
              if (!center) return null;
              
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                  <Avatar
                    src={center.logo || undefined}
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: center.color,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    {center.code}
                  </Avatar>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.65rem',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        lineHeight: 1,
                      }}
                    >
                      CENTER
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        lineHeight: 1.2,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {center.name}
                    </Typography>
                  </Box>
                </Box>
              );
            }}
          >
            {loadingCenters ? (
              <MenuItem disabled>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                  <CircularProgress size={16} />
                  <Typography>Loading centers...</Typography>
                </Box>
              </MenuItem>
            ) : error ? (
              <MenuItem disabled>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, width: '100%' }}>
                  <Typography color="error" variant="body2" sx={{ flex: 1 }}>
                    {error}
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={handleRefresh}
                    sx={{ p: 0.5 }}
                  >
                    <Typography variant="caption" color="primary">
                      Retry
                    </Typography>
                  </IconButton>
                </Box>
              </MenuItem>
            ) : centerOptions.length === 0 ? (
              <MenuItem disabled>
                <Typography color="text.secondary">No centers available</Typography>
              </MenuItem>
            ) : (
              centerOptions.map((center) => (
                <MenuItem
                  key={center.id ?? `${center.code}-${center.name}`}
                  value={center.id}
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.lighter,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.lighter,
                      },
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Avatar
                      src={center.logo || undefined}
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: center.color,
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {center.code}
                    </Avatar>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {center.name}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      ) : (
        // Simple box when no centers are available
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 1,
            px: 1.5,
            backgroundColor: theme.palette.background.default,
            boxShadow: theme.shadows[1],
            borderRadius: 1,
            minHeight: 48, // Match the select height
          }}
        >
          {showInlineLoading ? (
            <>
              <CircularProgress size={20} color="primary" />
              <Typography variant="body2" color="text.secondary">
                Switching center...
              </Typography>
            </>
          ) : error ? (
            <>
              <Typography variant="body2" color="error" sx={{ fontSize: '0.75rem' }}>
                Error loading centers
              </Typography>
              <IconButton 
                size="small" 
                onClick={handleRefresh}
                sx={{ p: 0.5 }}
              >
                <Typography variant="caption" color="primary">
                  Retry
                </Typography>
              </IconButton>
            </>
          ) : (
            <>
              <Avatar
                src={currentLocation.logo || undefined}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: currentLocation.color,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {currentLocation.code}
              </Avatar>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.65rem',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    lineHeight: 1,
                  }}
                >
                  CENTER
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    lineHeight: 1.2,
                    color: theme.palette.text.secondary,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {currentLocation.name}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CenterSelector; 